import { useState } from "react";
import { formatHebrewDate, hebrewDayLetters, hebrewYearLetters } from "../lib/hebrewGematria";
import { refToHebrew } from "../lib/hebrewRef";

const REF_DEMOS = [
  "Genesis 1:1",
  "Numbers 7:25",
  "Ruth 1:16-17",
  "Ecclesiastes 3:1-8",
  "Berakhot 5b",
  "Pirkei Avot 4:1",
  "Psalms 23",
  "I Kings 3:5",
  "Song of Songs 8:6",
  "Mishneh Torah, Repentance 2:1",
];

/**
 * Dev-only preview at /__gematria-preview — renders the gematria date
 * + Hebrew-ref helpers so we can validate the output in the live app
 * without needing real Stripe / Sefaria flows. Safe to leave shipped:
 * no auth, no data, no side-effects, just static text.
 */
export default function GematriaPreview() {
  const [iso, setIso] = useState("2026-05-20");
  const date = new Date(iso);
  const heDate = formatHebrewDate(date);
  const gregDate = date.toLocaleDateString("he-IL", {
    day: "numeric", month: "long", year: "numeric",
  });

  return (
    <div dir="rtl" className="max-w-3xl mx-auto p-8 space-y-8" data-testid="gematria-preview">
      <header className="space-y-1">
        <h1 className="font-heading text-2xl">תצוגה מקדימה — עברית באותיות</h1>
        <p className="text-sm text-muted-foreground">
          בדיקה של הפורמטרים של תאריך עברי ושמות מקורות. כל תצוגה ב-UI צריכה לעבור דרכם.
        </p>
      </header>

      <section className="space-y-4">
        <h2 className="font-heading text-lg">תאריך עברי</h2>
        <label className="block">
          <span className="text-sm text-muted-foreground">תאריך לועזי</span>
          <input
            type="date"
            value={iso}
            onChange={(e) => setIso(e.target.value)}
            className="block mt-1 border rounded-md px-3 py-2"
          />
        </label>
        <div className="border rounded-xl p-6 space-y-3 bg-card">
          <Row label="תאריך עברי" value={heDate} testid="row-hebrew-date" />
          <Row label="תאריך לועזי" value={gregDate} testid="row-gregorian-date" />
        </div>
        <div className="text-xs text-muted-foreground space-y-1">
          <div>יום בחודש: <strong>{hebrewDayLetters(date.getDate())}</strong></div>
          <div>שנה לדוגמה (תשפ״ו): <strong>{hebrewYearLetters(5786)}</strong></div>
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="font-heading text-lg">שמות מקורות (Refs)</h2>
        <p className="text-xs text-muted-foreground">
          מקורות מאוחסנים באנגלית (Sefaria slug) — כך הם מוצגים למשתמש בעברית:
        </p>
        <div className="border rounded-xl bg-card divide-y divide-border/40">
          {REF_DEMOS.map((src) => (
            <div key={src} className="flex justify-between items-baseline px-4 py-2.5">
              <code className="text-xs font-mono text-muted-foreground" dir="ltr">{src}</code>
              <span className="font-heading text-base" data-testid={`ref-${src}`}>
                {refToHebrew(src)}
              </span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function Row({ label, value, testid }) {
  return (
    <div className="flex justify-between items-baseline border-b border-border/40 pb-2 last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="font-heading text-lg" data-testid={testid}>{value}</span>
    </div>
  );
}
